import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Search, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });
const FIELDS = [
  ['office_address', 'Office address'], ['map_url', 'Google Maps link'], ['phone', 'Phone'], ['email', 'Email'],
  ['president', 'President'], ['secretary', 'General Secretary'], ['youth_coordinator', 'Youth Coordinator'],
];

const ChapterEditor = ({ row, onSaved }) => {
  const [f, setF] = useState(row);
  const save = async () => {
    const body = Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || null]));
    const res = await fetch(`${BASE}/districts/${encodeURIComponent(row.district)}`, { method: 'PUT', headers: auth(), body: JSON.stringify(body) });
    if (res.ok) { toast.success(`${row.district} saved`); onSaved(); } else toast.error('Save failed');
  };
  return (
    <Card data-testid="chapter-editor">
      <CardHeader><CardTitle>{row.district} chapter</CardTitle></CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        {FIELDS.map(([k, label]) => <Input key={k} placeholder={label} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`chapter-${k}`} />)}
        <Button className="bg-party-green hover:bg-green-700 sm:col-span-2" onClick={save} data-testid="chapter-save"><Save className="mr-2 h-4 w-4" /> Save chapter</Button>
        <p className="text-xs text-gray-500 sm:col-span-2">A district shows as "Active" once an office address or president is set.</p>
      </CardContent>
    </Card>
  );
};

export default function ManageDistricts() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState('');

  const load = () => {
    fetch(`${BASE}/districts`, { headers: auth() }).then((r) => { if (r.status === 401 || r.status === 403) navigate('/admin/login'); return r.json(); }).then((d) => Array.isArray(d) && setRows(d));
    fetch(`${BASE}/organiser-requests`, { headers: auth() }).then((r) => r.json()).then((d) => Array.isArray(d) && setOrgs(d));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const list = rows.filter((r) => r.district.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="min-h-screen bg-gray-100" data-testid="manage-districts-page">
      <header className="bg-white shadow-md">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="mr-2 h-4 w-4" /> Dashboard</Button>
          <h1 className="text-2xl font-bold text-gray-900">District Chapters</h1>
        </div>
      </header>
      <main className="mx-auto grid max-w-6xl gap-6 px-4 py-8 lg:grid-cols-3">
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border bg-white px-3"><Search className="h-4 w-4 text-gray-400" /><input className="flex-1 py-2 text-sm outline-none" placeholder="Search district" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-district-search" /></div>
          <div className="max-h-[70vh] space-y-1 overflow-y-auto rounded-lg border bg-white p-2">
            {list.map((r) => (
              <button key={r.district} onClick={() => setSel(r)} data-testid={`admin-district-${r.district.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm hover:bg-gray-50 ${sel?.district === r.district ? 'bg-party-red/10 font-bold' : ''}`}>
                {r.district}
                <span className={`h-2 w-2 rounded-full ${r.office_address || r.president ? 'bg-green-500' : 'bg-amber-400'}`} />
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-6 lg:col-span-2">
          {sel ? <ChapterEditor key={sel.district} row={sel} onSaved={load} /> : <p className="rounded-lg border bg-white p-8 text-center text-gray-500">Select a district to add office & leader details.</p>}
          <Card data-testid="organiser-requests">
            <CardHeader><CardTitle>Organiser volunteers ({orgs.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {orgs.length === 0 && <p className="text-sm text-gray-500">No requests yet.</p>}
              {orgs.map((o) => (
                <div key={o._id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-3 text-sm" data-testid={`organiser-request-${o._id}`}>
                  <div><b>{o.name}</b> · {o.district}{o.upazila ? ` / ${o.upazila}` : ''}<p className="text-xs text-gray-400">{new Date(o.created_at).toLocaleString()}</p></div>
                  <a href={`tel:${o.mobile}`} className="inline-flex items-center gap-1 text-party-green"><Phone className="h-4 w-4" /> {o.mobile}</a>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
